import {
  CardGiftcardOutlined,
  ExtensionOutlined,
  InboxOutlined,
  PeopleOutlined,
  LocationOffOutlined,
} from "@mui/icons-material";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import type { JSX } from "react";

type EmptyStateVariant =
  | "customers"
  | "privileges"
  | "addons"
  | "addresses"
  | "activities"
  | "default";

type PropsType = {
  variant?: EmptyStateVariant;
  title?: string;
  description?: string;
  icon?: JSX.Element;
  actionLabel?: string;
  onAction?: () => void;
};

const variantConfig: Record<
  EmptyStateVariant,
  { icon: JSX.Element; title: string; description: string }
> = {
  customers: {
    icon: <PeopleOutlined sx={{ fontSize: { xs: 32, md: 48 } }} />,
    title: "No customers found",
    description: "Start by adding your first customer to the CRM.",
  },
  privileges: {
    icon: <CardGiftcardOutlined sx={{ fontSize: { xs: 32, md: 48 } }} />,
    title: "No privileges found",
    description: "Start by adding your first privilege to the CRM.",
  },
  addons: {
    icon: <ExtensionOutlined sx={{ fontSize: { xs: 32, md: 48 } }} />,
    title: "No add-ons found",
    description: "Start by adding your first add-on to the CRM.",
  },
  addresses: {
    icon: <LocationOffOutlined sx={{ fontSize: { xs: 32, md: 48 } }} />,
    title: "No addresses found",
    description: "Start by adding your first address to the CRM.",
  },
  activities: {
    icon: <InboxOutlined sx={{ fontSize: { xs: 32, md: 48 } }} />,
    title: "No recent activities",
    description:
      "Activities will appear as you interact with customers and deals.",
  },
  default: {
    icon: <InboxOutlined sx={{ fontSize: { xs: 32, md: 48 } }} />,
    title: "No data available",
    description: "There is no data to display at this time.",
  },
};

const EmptyState = ({
  variant = "default",
  title,
  description,
  icon,
  actionLabel,
  onAction,
}: PropsType) => {
  const theme = useTheme();
  const config = variantConfig[variant];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 4, md: 8 },
        px: { xs: 2, md: 3 },
        minHeight: { xs: 200, md: 300 },
      }}
    >
      <Stack spacing={2} alignItems="center" textAlign="center">
        <Box
          sx={{
            color: theme.vars.palette.grey[400],
            mb: 1,
          }}
        >
          {icon || config.icon}
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.vars.palette.text.primary,
          }}
        >
          {title || config.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.vars.palette.text.secondary,
            maxWidth: 400,
          }}
        >
          {description || config.description}
        </Typography>
        {actionLabel && onAction && (
          <Button
            variant="contained"
            color="primary"
            onClick={onAction}
            sx={{ mt: 2 }}
          >
            {actionLabel}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default EmptyState;
